/**
 * Reine Berechnungs-Helfer fuer das Investitionen-Modul: Positions- und
 * Bereichs-Summen inklusive optionaler Aktien-Depot-Verknuepfung. Bewusst ohne
 * Redux/React, damit Uebersicht, Detailseite und Jahresuebersicht dieselbe Logik
 * teilen (DRY). Waehrungsumrechnung findet nicht statt – Summen sind je Waehrung
 * des Bereichs zu verstehen.
 */

/** Investierter Betrag einer Position (oder 0, wenn nicht gepflegt). */
export const positionInvestiert = (pos) => (pos.investiert != null ? Number(pos.investiert) : 0);

/**
 * Aktueller Wert einer Position: bei ISIN-Verknuepfung live aus dem Depot,
 * sonst der manuell gepflegte Wert. Fehlt beides, gilt der investierte Betrag
 * (also 0 Gewinn/Verlust), damit Summen nicht verfaelscht werden.
 */
export const positionWert = (pos, depotWerte = {}) => {
  const verknuepft = pos.isin ? depotWerte[pos.isin] : null;
  if (verknuepft && verknuepft.aktuellerWert != null) return Number(verknuepft.aktuellerWert);
  if (pos.aktuellerWert != null) return Number(pos.aktuellerWert);
  return positionInvestiert(pos);
};

/** Summen (investiert, Wert, G/V absolut und in %) eines Bereichs. */
export const bereichSummen = (bereich, depotWerte = {}) => {
  const positionen = bereich.positionen || [];
  const investiert = positionen.reduce((s, p) => s + positionInvestiert(p), 0);
  const wert = positionen.reduce((s, p) => s + positionWert(p, depotWerte), 0);
  const gv = wert - investiert;
  return { investiert, wert, gv, gvProz: investiert > 0 ? (gv / investiert) * 100 : null };
};

/** Fasst mehrere Bereiche je Waehrung zu Gesamt-Kennzahlen zusammen. */
export const summenJeWaehrung = (bereiche, depotWerte = {}) => {
  const map = {};
  bereiche.forEach((b) => {
    const s = bereichSummen(b, depotWerte);
    const w = b.waehrung || 'EUR';
    if (!map[w]) map[w] = { investiert: 0, wert: 0, gv: 0 };
    map[w].investiert += s.investiert;
    map[w].wert += s.wert;
    map[w].gv += s.gv;
  });
  return Object.entries(map).map(([waehrung, s]) => ({
    waehrung,
    ...s,
    gvProz: s.investiert > 0 ? (s.gv / s.investiert) * 100 : null,
  }));
};

/** Aggregiert die Bereichswerte nach Kategorie (fuer die Allokations-Anzeige). */
export const wertJeKategorie = (bereiche, depotWerte = {}) => {
  const map = {};
  bereiche.forEach((b) => {
    const { wert } = bereichSummen(b, depotWerte);
    const kat = b.kategorie || 'Ohne Kategorie';
    map[kat] = (map[kat] || 0) + wert;
  });
  return Object.entries(map)
    .map(([kategorie, wert]) => ({ kategorie, wert }))
    .filter((e) => e.wert > 0)
    .sort((a, b) => b.wert - a.wert);
};

/** Jahr aus einem ISO-Datum (oder "Ohne Jahr"). */
const jahrVon = (datum) => (datum ? String(new Date(datum).getFullYear()) : 'Ohne Jahr');

/**
 * Aggregiert alle Positionen aller Bereiche nach Anlage-Jahr (aus dem Positions-
 * Datum): investiert, aktueller Wert und Gewinn/Verlust je Jahr. Waehrungen
 * werden nicht umgerechnet – die Summen sind als Naeherung zu verstehen.
 */
export const investitionenJeJahr = (bereiche, depotWerte = {}) => {
  const map = {};
  bereiche.forEach((b) => {
    (b.positionen || []).forEach((p) => {
      const jahr = jahrVon(p.datum);
      if (!map[jahr]) map[jahr] = { investiert: 0, wert: 0 };
      map[jahr].investiert += positionInvestiert(p);
      map[jahr].wert += positionWert(p, depotWerte);
    });
  });
  return Object.entries(map)
    .map(([jahr, s]) => ({
      jahr,
      investiert: s.investiert,
      wert: s.wert,
      gv: s.wert - s.investiert,
      gvProz: s.investiert > 0 ? ((s.wert - s.investiert) / s.investiert) * 100 : null,
    }))
    .sort((a, b) => String(b.jahr).localeCompare(String(a.jahr)));
};

/** Byte-Zahl menschenlesbar (B/KB/MB). */
export const formatiereGroesse = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
